/**
 * Configuration d'exécution, lue une seule fois depuis les variables Vite et validée.
 * Une variable manquante fait échouer le démarrage avec un message explicite plutôt
 * qu'une erreur réseau obscure plus tard.
 */

export type AppEnvironment = 'development' | 'production'

export interface AppConfig {
  environment: AppEnvironment
  supabase: {
    url: string
    /** Clé publiable (anciennement « anon ») : publique par nature, protégée par la RLS. */
    publishableKey: string
  }
  /** Clé publique Web Push (VAPID). Absente : l'activation des notifications push est masquée. */
  vapidPublicKey: string | null
}

type RawEnv = Record<string, string | boolean | undefined>

const ENVIRONMENTS: readonly AppEnvironment[] = ['development', 'production']

export class ConfigError extends Error {
  override name = 'ConfigError'
}

function readString(env: RawEnv, key: string): string {
  const value = env[key]
  if (typeof value !== 'string' || value.trim() === '') {
    throw new ConfigError(`Variable d’environnement manquante : ${key}`)
  }
  return value.trim()
}

function readOptional(env: RawEnv, key: string): string | null {
  const value = env[key]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

function isEnvironment(value: string): value is AppEnvironment {
  return (ENVIRONMENTS as readonly string[]).includes(value)
}

function readSupabaseUrl(env: RawEnv, environment: AppEnvironment): string {
  const raw = readString(env, 'VITE_SUPABASE_URL')
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    throw new ConfigError(`VITE_SUPABASE_URL invalide : « ${raw} »`)
  }
  const isLocal = url.hostname === '127.0.0.1' || url.hostname === 'localhost'
  if (environment === 'production' && (isLocal || url.protocol !== 'https:')) {
    throw new ConfigError('VITE_SUPABASE_URL doit être une URL https distante en production.')
  }
  return url.origin
}

export function parseConfig(env: RawEnv): AppConfig {
  const environment = readString(env, 'VITE_APP_ENV')
  if (!isEnvironment(environment)) {
    throw new ConfigError(
      `VITE_APP_ENV invalide : « ${environment} ». Valeurs acceptées : ${ENVIRONMENTS.join(', ')}`,
    )
  }

  return {
    environment,
    supabase: {
      url: readSupabaseUrl(env, environment),
      publishableKey: readString(env, 'VITE_SUPABASE_PUBLISHABLE_KEY'),
    },
    vapidPublicKey: readOptional(env, 'VITE_VAPID_PUBLIC_KEY'),
  }
}
